import React, { useMemo } from 'react';
import { IndicatorType } from './types';
import { Icon, Tooltip } from '../../../../shared/ui';
import { IconMap } from '../../../../shared/sprite';

type CommentIndicatorProps = {
  count: number;
  threadIds: string[];
  top: number;
  onClick: (threadIds: string[]) => void;
  offset: number;
};

const CommentIndicator: React.FC<CommentIndicatorProps> = ({
  count,
  threadIds,
  top,
  onClick,
  offset,
}) => {
  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (onClick) {
      onClick(threadIds);
    }
  };

  return (
    <div
      className="absolute flex items-center justify-center cursor-pointer pointer-events-auto"
      style={{
        top: `${top}px`,
        right: `${offset}px`,
      }}
      onClick={handleClick}
    >
      <Tooltip
        hideArrow
        alignOffset={4}
        trigger={
          <button className="flex items-center gap-1 border-none outline-none rounded-md hover:bg-gray-50 p-1.5">
            <Icon glyph={IconMap.MessageSquareGreen02} width={24} />
            <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-gray-900 text-xs">
              {count}
            </span>
          </button>
        }
      >
        View comments
      </Tooltip>
    </div>
  );
};

type CommentIndicatorOverlayProps = {
  indicators: IndicatorType[];
  onClick: (threadIds: string[]) => void;
  offset: number;
};

type IndicatorPosition = {
  adjustedTop: number;
  count: number;
  pos: number;
  threadIds: string[];
  top: number;
};

export const CommentIndicatorOverlay: React.FC<
  CommentIndicatorOverlayProps
> = ({ indicators = [], onClick, offset }) => {
  const positionedIndicators = useMemo(() => {
    const sorted = [...indicators].sort((a, b) => a.top - b.top);
    const positioned = [] as IndicatorPosition[];
    const minSpacing = 36; // Minimum pixels between indicators

    sorted.forEach((indicator, index) => {
      let adjustedTop = indicator.top;

      if (index > 0) {
        const prevIndicator = positioned[index - 1];
        if (adjustedTop < prevIndicator.adjustedTop + minSpacing) {
          adjustedTop = prevIndicator.adjustedTop + minSpacing;
        }
      }

      positioned.push({
        ...indicator,
        adjustedTop,
      });
    });

    return positioned;
  }, [indicators]);

  return (
    <>
      {positionedIndicators.map((indicator, index) => (
        <CommentIndicator
          key={`${indicator.pos}-${index}`}
          count={indicator.count}
          threadIds={indicator.threadIds}
          top={indicator.adjustedTop}
          offset={offset}
          onClick={onClick}
        />
      ))}
    </>
  );
};
